// Path: frontend/src/hooks/useDebounce.ts
import { useEffect, useState } from 'react';

/**
 * A custom hook that delays updating a value until a certain amount of time has passed.
 * This is useful for preventing API calls on every keystroke in a search input.
 * @param value The value to debounce (e.g., the text from an input).
 * @param delay The debounce delay in milliseconds.
 * @returns The debounced value.
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up a timer to update the debounced value after the specified delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer if the value changes (e.g., user keeps typing)
    // or if the component unmounts.
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]); // Only re-call effect if value or delay changes

  return debouncedValue;
}
